// src/constants.ts
var ELIGIBILITY_STATUS = {
  ELIGIBLE: "eligible",
  INELIGIBLE: "ineligible",
  OVERRIDE_ELIGIBLE: "override_eligible"
};
var SUBMISSION_STATUS = {
  NOT_SUBMITTED: "not_submitted",
  SUBMITTED: "submitted",
  ACCEPTED: "accepted",
  PAID: "paid",
  REJECTED: "rejected"
};
var LEDGER_TYPE = {
  PROGRAM_PAYMENT: "program_payment",
  REIMBURSEMENT: "reimbursement",
  RECOUP: "recoup"
};
var LEDGER_SOURCE_TYPE = {
  MOVE: "move",
  ACCESSORIAL: "accessorial"
};
var SUBMITTED_STATUSES = [
  SUBMISSION_STATUS.SUBMITTED,
  SUBMISSION_STATUS.ACCEPTED,
  SUBMISSION_STATUS.PAID,
  SUBMISSION_STATUS.REJECTED
];
var ANY_SUBMITTED = "any_submitted";
var ANY_SUBMITTED_LABEL = "Submitted (any status)";
var SUBMISSION_LABELS = {
  [SUBMISSION_STATUS.NOT_SUBMITTED]: "Not submitted",
  [SUBMISSION_STATUS.SUBMITTED]: "Submitted",
  [SUBMISSION_STATUS.ACCEPTED]: "Accepted",
  [SUBMISSION_STATUS.PAID]: "Paid",
  [SUBMISSION_STATUS.REJECTED]: "Rejected"
};
var DATE_PRESET = {
  LAST_30: "last_30",
  LAST_60: "last_60",
  LAST_90: "last_90",
  CUSTOM: "custom"
};
var DEFAULT_DATE_PRESET = DATE_PRESET.LAST_30;
var DATE_PRESET_OPTIONS = [
  { value: DATE_PRESET.LAST_30, label: "Last 30 days", days: 30 },
  { value: DATE_PRESET.LAST_60, label: "Last 60 days", days: 60 },
  { value: DATE_PRESET.LAST_90, label: "Last 90 days", days: 90 },
  { value: DATE_PRESET.CUSTOM, label: "Custom range" }
];
var CLAIMS_FILTER = {
  SEARCH: "search",
  ELIGIBILITY: "eligibility",
  SUBMISSION: "submission",
  FAILED_RULE: "failed_rule",
  CUSTOMERS: "customers",
  SOURCES: "sources",
  DATE: "date"
};
var PLACEHOLDER = "\u2014";
var ESTIMATE_MARK = " *";

// src/utils/rooftops.ts
var UNKNOWN_ROOFTOP_ID = 0;
var UNKNOWN_ROOFTOP_NAME = "Unknown rooftop";
var byRooftopName = (a, b) => a.rooftopName.localeCompare(b.rooftopName) || a.rooftopId - b.rooftopId;

// src/utils/ledgerSource.ts
import { LANE_TOLL_ACCESSORIAL_ID, TOLL_ACCESSORIAL_CODE } from "@hopdrive/sdk-ar";
var MOVE_COST_SOURCE_LABEL = "Move cost";
var UNNAMED_ACCESSORIAL_LABEL = "Accessorial";
var accessorialName = (code) => {
  const words = code.replace(/[_-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : code;
};
var ledgerSourceLabel = (entry) => {
  if (entry.source_type !== LEDGER_SOURCE_TYPE.ACCESSORIAL) return MOVE_COST_SOURCE_LABEL;
  if (String(entry.source_id) === String(LANE_TOLL_ACCESSORIAL_ID)) return accessorialName(TOLL_ACCESSORIAL_CODE);
  const code = entry.move?.accessorials?.find((accessorial) => String(accessorial.id) === String(entry.source_id))?.code;
  return code ? accessorialName(code) : UNNAMED_ACCESSORIAL_LABEL;
};

// src/utils/format.ts
var CURRENCY = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
var COUNT = new Intl.NumberFormat("en-US");
var DATE = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });
var toAmount = (value) => {
  if (value === null || value === void 0 || value === "") return 0;
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n;
};
var formatCurrency = (value) => {
  if (value === null || value === void 0 || value === "") return PLACEHOLDER;
  const n = Number(value);
  return Number.isNaN(n) ? PLACEHOLDER : CURRENCY.format(n);
};
var formatCount = (count) => COUNT.format(count);
var MILES = new Intl.NumberFormat("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
var formatDistance = (miles) => {
  if (miles === null || miles === void 0 || miles === "") return PLACEHOLDER;
  const n = Number(miles);
  return Number.isNaN(n) ? PLACEHOLDER : `${MILES.format(n)} mi`;
};
var ODOMETER_PATTERN = /^[0-9]+\.?[0-9]*$/;
var formatOdometer = (reading) => {
  if (typeof reading !== "number" && typeof reading !== "string") return null;
  const value = String(reading).trim();
  if (!ODOMETER_PATTERN.test(value)) return null;
  return `${formatCount(Math.round(Number(value)))} mi`;
};
var formatCountOf = (count, noun) => `${formatCount(count)} ${noun}${count === 1 ? "" : "s"}`;
var formatDate = (value) => {
  if (!value) return PLACEHOLDER;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? PLACEHOLDER : DATE.format(date);
};
var describeVehicle = (move) => [move?.vehicle_year, move?.vehicle_make, move?.vehicle_model].filter(Boolean).join(" ") || PLACEHOLDER;

// src/utils/ledgerMoney.ts
var ledgerAmount = (row) => toAmount(row.actual_amount ?? row.estimated_amount);
var legTotal = (ledger, type, amountOf) => {
  let total = null;
  for (const row of ledger ?? []) if (row.type === type) total = (total ?? 0) + amountOf(row);
  return total;
};
var isLedgerEstimated = (ledger) => (ledger ?? []).some((row) => !row.confirmed);
var groupLedgerBySource = (ledger) => {
  const groups = /* @__PURE__ */ new Map();
  for (const row of ledger ?? []) {
    const key = `${row.move_id}|${row.source_type}|${row.source_id}`;
    let group = groups.get(key);
    if (!group) {
      group = { key, moveId: row.move_id, label: ledgerSourceLabel(row), rows: [] };
      groups.set(key, group);
    }
    group.rows.push(row);
  }
  return [...groups.values()];
};

export {
  ELIGIBILITY_STATUS,
  SUBMISSION_STATUS,
  LEDGER_TYPE,
  LEDGER_SOURCE_TYPE,
  SUBMITTED_STATUSES,
  ANY_SUBMITTED,
  ANY_SUBMITTED_LABEL,
  SUBMISSION_LABELS,
  DATE_PRESET,
  DEFAULT_DATE_PRESET,
  DATE_PRESET_OPTIONS,
  CLAIMS_FILTER,
  PLACEHOLDER,
  ESTIMATE_MARK,
  UNKNOWN_ROOFTOP_ID,
  UNKNOWN_ROOFTOP_NAME,
  byRooftopName,
  MOVE_COST_SOURCE_LABEL,
  accessorialName,
  ledgerSourceLabel,
  toAmount,
  formatCurrency,
  formatCount,
  formatDistance,
  formatOdometer,
  formatCountOf,
  formatDate,
  describeVehicle,
  ledgerAmount,
  legTotal,
  isLedgerEstimated,
  groupLedgerBySource
};
//# sourceMappingURL=chunk-QBQOJXLM.mjs.map